import { defineStore } from 'pinia';
import { computed } from 'vue';

import { dashboardBffClient } from '@/api-client';
import type { ProfileCRForm } from '@/api-client/api';
import { permissionsErrorHandler } from '@/authorization';
import { useApiRequest } from '@/composables/useApiRequest';
import constants from '@/constants';
import { defaultErrorHandler } from '@/services';

import { useAppInfoStore } from '../appInfo';

type ProfilesEndpointQuery = Parameters<typeof dashboardBffClient.bff.bffListProfilesCr>[1];

export const useProfileStore = defineStore('profile', () => {
  const appInfoStore = useAppInfoStore();

  const {
    request: _fetchProfiles,
    getResponseByArgs,
    forcefullyRequest: _forceFetchProfiles,
    updateByArgs,
  } = useApiRequest(dashboardBffClient.bff.bffListProfilesCr, {
    transformResponse: (response) => response.data,
    cacheKey: (namespace) => namespace,
    cacheTimeout: 30 * 1000,
    onRejected: (err) => permissionsErrorHandler(err, 'profiles', 'list'),
  });

  // DO NOT SET THIS TO AN EMPTY ARRAY BY DEFAULT!
  // There are several places that we set loading states based on the profiles being undefined.
  // Once the profiles are fetched, this will be set to an empty array.

  const profiles = computed(() => getResponseByArgs(appInfoStore.systemNamespace));
  const profilesGlobal = computed(() => getResponseByArgs(constants.MULTICLUSTER_NAMESPACE));

  const profilesLocation = computed(() =>
    profiles.value?.filter((p) => p.spec.type === 'Location'),
  );

  const immutableLocationProfilesSet = computed(() => {
    const profileNames = profilesLocation.value?.reduce((acc: string[], lp) => {
      const pp = lp.spec.locationSpec?.objectStore?.protectionPeriod;
      if (pp && pp !== '0') {
        const name = lp.metadata?.name;
        if (name) acc.push(name);
      }
      return acc;
    }, []);

    return new Set(profileNames);
  });

  const profilesInfra = computed(() => profiles.value?.filter((p) => p.spec.type === 'Infra'));

  const infraProvidersSet = computed(() => {
    const profileNames = profilesInfra.value?.reduce((acc: string[], ip) => {
      const providerName = ip.spec.infra?.type;
      acc.push(providerName ?? '');

      return acc;
    }, []);

    return new Set(profileNames);
  });

  const infraGlobalProvidersSet = computed(() => {
    const profileNames = profilesGlobalInfra.value?.reduce((acc: string[], ip) => {
      const providerName = ip.spec.infra?.type;
      acc.push(providerName ?? '');

      return acc;
    }, []);

    return new Set(profileNames);
  });

  const profilesGlobalLocation = computed(() =>
    profilesGlobal.value?.filter((p) => p.spec.type === 'Location'),
  );

  const profilesGlobalInfra = computed(() =>
    profilesGlobal.value?.filter((p) => p.spec.type === 'Infra'),
  );

  const getAllVmWareProfiles = computed(() => {
    return profilesInfra.value?.filter((profile) => profile.spec.infra?.vsphere);
  });

  const hasVMWareProfiles = computed(() => {
    return !!getAllVmWareProfiles.value?.length;
  });

  const isProfileImmutable = (profileName: string) => {
    return immutableLocationProfilesSet.value.has(profileName);
  };

  async function fetchProfiles(query?: ProfilesEndpointQuery) {
    await _fetchProfiles(appInfoStore.systemNamespace, query);
  }

  async function fetchGlobalProfiles(query?: ProfilesEndpointQuery) {
    await _fetchProfiles(constants.MULTICLUSTER_NAMESPACE, query);
  }

  async function forcefullyFetchProfiles(query?: ProfilesEndpointQuery) {
    await _forceFetchProfiles(appInfoStore.systemNamespace, query);
  }

  async function forcefullyFetchGlobalProfiles(query?: ProfilesEndpointQuery) {
    await _forceFetchProfiles(constants.MULTICLUSTER_NAMESPACE, query);
  }

  async function updateProfile(profileName: string, payload: ProfileCRForm) {
    await dashboardBffClient.bff.bffUpdateProfileCr(
      appInfoStore.systemNamespace,
      profileName,
      payload,
    );
    await forcefullyFetchProfiles();
    await forcefullyFetchGlobalProfiles();
  }

  const {
    request: deleteProfile,
    isPending: isDeleteProfilePending,
    response: deleteResponse,
  } = useApiRequest(dashboardBffClient.bff.bffDeleteProfileCr, { onRejected: defaultErrorHandler });

  return {
    profiles,
    profilesGlobal,
    profilesLocation,
    immutableLocationProfilesSet,
    infraProvidersSet,
    infraGlobalProvidersSet,
    profilesInfra,
    profilesGlobalLocation,
    profilesGlobalInfra,
    hasVMWareProfiles,
    isProfileImmutable,
    fetchProfiles,
    fetchGlobalProfiles,
    forcefullyFetchProfiles,
    forcefullyFetchGlobalProfiles,
    updateProfile,
    updateByArgs,
    deleteProfile,
    deleteResponse,
    isDeleteProfilePending,
  };
});
